import { CustomAction, EmbedContextOptions } from '@thoughtspot/blink-context';
import {
    ContextMenuInputPoint,
    ContextMenuInputPoints,
    ContextMenuInputValue,
} from '@thoughtspot/chart-common';
import {
    getChartType,
    getColumn,
} from '@thoughtspot/chart-lib/lib/core/chart-model-utils';
import { isCustomOrTsHostedChart } from '@thoughtspot/chart-lib/lib/core/chart-type-specification-service';
import { getName } from '@thoughtspot/chart-lib/lib/core/column-name-util';
import {
    getDisplayString,
    getStartEpoch,
} from '@thoughtspot/custom-calendar-service';
import {
    convertToMilliSeconds,
    formatDate,
} from '@thoughtspot/date-format-util';
import { getFormattedValue } from '@thoughtspot/formatter-service';
import { translate } from '@thoughtspot/i18n';
import {
    collectEvent,
    UserAction,
    userActionTypeEnum,
} from '@thoughtspot/metrics';
import {
    ActionMenuItem,
    ActionMenuItemGroup,
} from '@thoughtspot/radiant-react/widgets/action-menu/action-menu';
import { SessionService } from '@thoughtspot/session-service';
import {
    getEmptyValuePlaceHolder,
    getNullValuePlaceHolder,
} from '@thoughtspot/special-data-value-format';
import { ChartConfigParameters, VisualConfig } from '@thoughtspot/ts-chart-sdk';
import {
    getBucketization,
    getId,
    hasCustomCalendar,
    isDateColumn,
    isFilterColumn,
    isGrowth,
    isTimeColumn,
} from '@thoughtspot/visualization-column-util';
import _, { isEmpty } from 'lodash';
import {
    AnswerPageContext,
    getUserSelectionPoints,
} from '/@services/answer/answer.util';
import {
    AccessMode,
    AnswerColumn,
    ChartType,
    ChartViz,
    ColumnTimeBucket,
    ColumnValueType,
    DisplayMode,
    EmbedActionConfig,
    FalconDataType,
    FilterColumnValueInput,
    FilterContentInput,
    FilterGroup,
    FilterGroupId,
    FilterTypes,
    LinkedFilterColumn,
    LogicalTableTypeEnum,
    ObjectPermission,
    ObjectType,
    PinboardSourceDetail,
    TableViz,
} from '/@services/generated/graphql-types';
import { MetricsService } from '/@services/metrics-service/metrics-service';
import {
    Action,
    addActionItemMapFromUrlParams,
    getDisabledActionReason,
    getDisabledActions,
    getEmbedActionStateMap,
    getHiddenActions,
    getVisibleActions,
    shouldDisableForEmbed,
    shouldHideForEmbed,
} from '/@utils/embed.util';
import { getFilterContentCustomChartSdk } from '../../utils/viz/chart-action-menu.util';
import {
    getFormattedDateForApplySuggestions,
    shouldDisableCCAForDifferentColorValues,
} from '../change-analysis-modal/change-analysis.util';
import { canEditColumn } from '../column-configure/column-configure.util';
import { isInputTableVisible } from '../data-panel-v2/data-panel-v2.util';
import { getDrillFromColumnIds } from '../drilldown-menu/drilldown.util';
import {
    FILTER_TYPE,
    getAttributeFilterValue,
    getFilterType,
} from '../filters/answer-filter.util';
import {
    ADD_FILTER_SOURCES,
    collectAddFilterEvent,
    collectEventWithMixpanelData,
    collectInitiateFilterAdditionEvent,
    getMetricsColumnDataType,
    INITIATE_FILTER_ADDITION_SOURCES,
} from '../filters/answer-filter-metrics.util';
import {
    getAttributeFilterInput,
    stringToFilterColumnValueInput,
} from '../filters/attribute-filter/attribute-filter.util';
import {
    getDateRangeFilterContent,
    getDateRangeFilterInput,
} from '../filters/date-range-filter/date-range-filter.util';

const CONTEXT_MENU = 'CONTEXT_MENU';

export const COLLAPSE_MENU = 'COLLAPSE_MENU';
export const EXPAND_MENU = 'EXPAND_MENU';
export const SORT_MENU = 'SORT_MENU';

export const CONTEXT_MENU_ITEM_SEPARATOR = 'context-menu-item-separator';
export const PIVOT_CONTEXT_MENU_SORT = 'pivot-context-menu-sort';
export const PIVOT_CONTEXT_MENU_CLEAR_SORT = 'pivot-context-menu-clear-sort';

export interface ContextMenuInput {
    points: ContextMenuInputPoints;
    menuItems: ActionMenuItemGroup[];
}

export enum GenericMenuItemType {
    SHOW_UNDERLYING_DATA = 'context-menu-item-show-underlying-data',
    INCLUDE = 'context-menu-item-include',
    EXCLUDE = 'context-menu-item-exclude',
    SPOTIQ_ANALYZE = 'context-menu-item-spotiq-analyze',
    COPY_TO_CLIPBOARD = 'context-menu-item-copy-to-clipboard',
    EXPLORE = 'context-menu-item-explore',
    DOWNLOAD = 'context-menu-item-download',
    EDIT = 'context-menu-item-edit',
    COPY_AND_EDIT = 'context-menu-item-copy-and-edit',
    EDIT_MEASURE = 'context-menu-item-edit-measure',
    SEPARATOR = 'context-menu-item-separator',
    PIVOT_HEADER_EXPAND = 'pivot-context-menu-header-expand',
    PIVOT_HEADER_COLLAPSE = 'pivot-context-menu-header-collapse',
    PIVOT_SORT = 'pivot-context-menu-sort',
    PIVOT_CLEAR_SORT = 'pivot-context-menu-clear-sort',
    CROSS_FILTER = 'context-menu-item-cross-filter',
    REMOVE_CROSS_FILTER = 'context-menu-item-remove-cross-filter',
    RUN_CHANGE_ANALYSIS = 'enableContextualChangeAnalysis',
    EXPLAIN_ANOMALY = 'context-menu-item-explain_anomaly',
    DO_ITERATIVE_ANALYSIS = 'enableIterativeChangeAnalysis',
}

export enum VizContextMenuOptionType {
    DRILL = 'DRILL',
    APPLY_AS_RUNTIME_FILTER = 'APPLY_AS_RUNTIME_FILTER',
    RELATED_ITEMS = 'RELATED_ITEMS',
    GENERIC = 'GENERIC',
}

export type FilterColumnAndContent = {
    filterContent: FilterContentInput[];
    filterGroup: FilterGroup;
};

export type ContextMenuOption = {
    id: GenericMenuItemType | VizContextMenuOptionType;
    isDisabled?: boolean;
    disabledExplanation?: string;
};

export type EnabledContextMenuOptionType = {
    enabled: boolean;
    explanation?: string;
};

export type FilterMapProps = {
    sourceContainerId: string;
    filterId: string;
    filterValues: string[];
};

const DefaultVizContextMenuOptionTypes: string[] = [
    GenericMenuItemType.CROSS_FILTER,
    GenericMenuItemType.REMOVE_CROSS_FILTER,
    GenericMenuItemType.EXCLUDE,
    GenericMenuItemType.INCLUDE,
    VizContextMenuOptionType.DRILL,
    GenericMenuItemType.DO_ITERATIVE_ANALYSIS,
    GenericMenuItemType.SHOW_UNDERLYING_DATA,
    GenericMenuItemType.SPOTIQ_ANALYZE,
    GenericMenuItemType.RUN_CHANGE_ANALYSIS,
];

// default context menu options for charts other than Pivot Table
export const DefaultVizContextMenuOptionTypesForChart: string[] = [
    ...DefaultVizContextMenuOptionTypes,
];

// default context menu options for Table
export const DefaultVizContextMenuOptionTypesForTable: string[] = [
    ...DefaultVizContextMenuOptionTypes,
    GenericMenuItemType.COPY_TO_CLIPBOARD,
];

// default context menu options for Pivot Table
export const DefaultVizContextMenuOptionTypesForPivotTable: string[] = [
    ...DefaultVizContextMenuOptionTypesForChart,
    GenericMenuItemType.COPY_TO_CLIPBOARD,
];

const VizContextMenuOptionsRequiringUnderlyingDataAccess: string[] = [
    VizContextMenuOptionType.DRILL,
    GenericMenuItemType.DO_ITERATIVE_ANALYSIS,
    GenericMenuItemType.SHOW_UNDERLYING_DATA,
    GenericMenuItemType.EXCLUDE,
    GenericMenuItemType.INCLUDE,
    GenericMenuItemType.SPOTIQ_ANALYZE,
    GenericMenuItemType.CROSS_FILTER,
    GenericMenuItemType.REMOVE_CROSS_FILTER,
    GenericMenuItemType.RUN_CHANGE_ANALYSIS,
];

export const dataTypeMapping: Record<string, string> = {
    [FalconDataType.Bool]: ColumnValueType.Bool,
    [FalconDataType.Char]: ColumnValueType.Char,
    [FalconDataType.Date]: ColumnValueType.Date,
    [FalconDataType.DateTime]: ColumnValueType.Date,
    [FalconDataType.Time]: ColumnValueType.Date,
    [FalconDataType.Int32]: ColumnValueType.Int,
    [FalconDataType.Int64]: ColumnValueType.Int,
    [FalconDataType.Double]: ColumnValueType.Double,
    [FalconDataType.Float]: ColumnValueType.Double,
};
export const filterColumnValueMapping: Record<string, string> = {
    [ColumnValueType.Bool]: 'boolValue',
    [ColumnValueType.Char]: 'stringValue',
    [ColumnValueType.Date]: 'intValue',
    [ColumnValueType.Double]: 'doubleValue',
    [ColumnValueType.Int]: 'intValue',
};

export enum MouseEventButton {
    Left = 0,
    Middle = 1,
    Right = 2,
}

/** To know the source of the context menu opened event either from left or right click */
export const getOpenContextMenuSource = (event: MouseEvent): string => {
    switch (event.button) {
        case MouseEventButton.Left:
            return 'left-click';
        case MouseEventButton.Right:
            return 'right-click';
        default:
            return '';
    }
};

export const getSelectedColumnId = (
    selectedAttribute: ContextMenuInputValue,
) => {
    return _.get(selectedAttribute, 'column.referencedColumns[0].guid');
};
export const getFilterColumnValueInput = (
    selectedColumnData: ContextMenuInputValue,
): FilterColumnValueInput | null => {
    const column: AnswerColumn = selectedColumnData.column;
    const dataType = dataTypeMapping[column.dataType] as ColumnValueType;
    const valueType = filterColumnValueMapping[dataType];
    if (!valueType || !dataType) {
        return null;
    }

    if (
        selectedColumnData.value === getNullValuePlaceHolder() ||
        (isDateColumn(column) && _.isNil(selectedColumnData.value))
    ) {
        return {
            type: dataType,
            isNull: true,
        };
    }

    if (hasCustomCalendar(column)) {
        const value =
            getStartEpoch(selectedColumnData.value) || selectedColumnData.value;
        return {
            type: dataType,
            [valueType]: value,
        };
    }

    if (
        ColumnValueType.Int === dataType ||
        ColumnValueType.Double === dataType ||
        ColumnValueType.Date === dataType
    ) {
        const value = parseFloat(selectedColumnData.value);

        return {
            type: dataType,
            [valueType]: value,
        };
    }
    return {
        type: dataType,
        [valueType]: selectedColumnData.value,
    };
};

export const getFilterContent = (
    selectedColumnData: ContextMenuInputValue,
): FilterContentInput => {
    const filterColumnValueInput = getFilterColumnValueInput(
        selectedColumnData,
    );

    return {
        filterType: FilterTypes.In,
        value: [filterColumnValueInput],
    };
};

export const getFilterContentEmbed = (
    menuItems: any,
    embedConfig: EmbedContextOptions,
) => {
    const embedDisabledActions = getDisabledActions(embedConfig);
    const embedDisabledReason = getDisabledActionReason(embedConfig);
    const embedHiddenActions = getHiddenActions(embedConfig);
    const embedVisibleActions = getVisibleActions(embedConfig);
    let filteredMenuItems: string[] = [];

    if (embedVisibleActions) {
        filteredMenuItems = Object.keys(menuItems).filter(key =>
            embedVisibleActions.has(key),
        );
        if (embedHiddenActions) {
            filteredMenuItems = filteredMenuItems.filter(
                key => !embedHiddenActions.has(key),
            );
        }
    }
    if (embedHiddenActions && !embedVisibleActions) {
        filteredMenuItems = Object.keys(menuItems).filter(
            key => !embedHiddenActions.has(key),
        );
    }
    return filteredMenuItems.reduce((_menuItems, key) => {
        return {
            ..._menuItems,
            [key]: {
                ...menuItems[key],
                itemDisabled:
                    menuItems[key].itemDisabled ||
                    embedDisabledActions?.has(key),
                itemDisabledTooltip: embedDisabledActions?.has(key)
                    ? embedDisabledReason
                    : menuItems[key].itemDisabledTooltip,
            },
        };
    }, {});
};

export const getFilterGroup = (
    column: AnswerColumn,
    isLogicalColumn = false,
): FilterGroup => {
    let groupId: FilterGroupId;
    if (isLogicalColumn) {
        groupId = {
            logicalColumnId: column?.referencedColumns[0]?.guid,
        };
    } else {
        groupId = {
            answerColumnId: column.id,
        };
    }
    return {
        groupId,
        columnInfo: {
            name: getName(column),
            dataType: dataTypeMapping[column.dataType],
            type: column?.type,
            timeBucket: column?.timeBucket,
        },
    } as FilterGroup;
};

const isColumnDateType = (column: AnswerColumn) =>
    isDateColumn(column) &&
    getBucketization(column) !== ColumnTimeBucket.NoBucket;

/**
 * get link filter map of primary column id by secondary column id
 * @param linkFilters linkFilters
 */
export const getPrimaryColumnBySecondaryColumn = (
    linkFilters: LinkedFilterColumn[],
) => {
    const linkFiltersMap = new Map<string, string>();
    _.forEach(linkFilters, linkFilter => {
        _.forEach(linkFilter.secondaryFilterColumns, secondaryFilterColumn => {
            linkFiltersMap.set(
                secondaryFilterColumn?.logicalColumnId,
                linkFilter?.primaryFilterColumn?.logicalColumnId,
            );
        });
    });
    return linkFiltersMap;
};

/**
 * Check if column can be filtered filterable on Liveboard
 * @param selectedAttribute selected attribute column points
 * @param dataSources liveboard data sources
 * @param filterValueByColumnIdMap filter values map by column id
 */
export const isColumnFilterable = (
    selectedAttribute: ContextMenuInputValue,
    dataSources: PinboardSourceDetail[],
    filterPropsByColumnIdMap: Map<string, FilterMapProps>,
) => {
    const { column, value } = selectedAttribute;
    const columnId = getSelectedColumnId(selectedAttribute);
    const timeBucket = getBucketization(column);
    // column is not filterable if column is formula or
    // column which is a combination of multiple logical column or
    // is it a date column or
    // column which is referenced from multiple tables
    if (
        column?.formulaId ||
        column?.referencedColumns?.length !== 1 ||
        isDateColumn(column) ||
        isTimeColumn(column) ||
        (!_.isNil(timeBucket) && timeBucket !== ColumnTimeBucket.NoBucket) ||
        column?.referencedTables?.length !== 1
    ) {
        return false;
    }
    // get column's data source info
    const dataSource = _.find(dataSources, [
        'id',
        column.referencedTables[0].guid,
    ]);
    // if column's data source doesn't exist or not of WorkSheet type
    if (!dataSource || dataSource?.type !== LogicalTableTypeEnum.Worksheet) {
        return false;
    }
    // if selected value is not already filtered
    const filterProps = filterPropsByColumnIdMap.get(columnId);
    if (!_.includes(filterProps?.filterValues, _.toLower(value))) {
        return true;
    }
    return false;
};

/**
 * get map of filter values by column id
 * @param filterGroups filter groups
 * @param containerId container id of visualization
 */
export const getFilterPropsByColumnIdMap = (
    filterGroups?: FilterGroup[],
    containerId?: string,
): Map<string, FilterMapProps> => {
    const filtersMap: Map<string, FilterMapProps> = new Map();
    _.forEach(filterGroups, filterGroup => {
        if (
            _.isUndefined(containerId) ||
            containerId === filterGroup.sourceContainerId
        ) {
            const logicalColumnId = filterGroup?.groupId?.logicalColumnId;
            const properties: FilterMapProps = {
                sourceContainerId: filterGroup?.sourceContainerId,
                filterId: filterGroup.filters[0]?.filterId,
                filterValues: filterGroup?.filters[0]?.filterContent[0].value.map(
                    getAttributeFilterValue,
                ),
            };
            filtersMap.set(logicalColumnId, properties);
        }
    });
    return filtersMap;
};

/**
 * get list of all filterable columns from list of selected column
 * @param selectedAttributes selected attribute column
 * @param dataSources liveboard data sources
 * @param filterGroups applied filters
 */
export const getFilterableColumns = (
    selectedAttributes: ContextMenuInputValue[],
    dataSources: PinboardSourceDetail[],
    filterGroups?: FilterGroup[],
) => {
    const filterValueMap = getFilterPropsByColumnIdMap(filterGroups);
    // get list of columns on which filter can be applied
    return _.filter(selectedAttributes, selectedAttribute =>
        isColumnFilterable(selectedAttribute, dataSources, filterValueMap),
    );
};

export const getContextMenuItemsConfig = (
    optionId: GenericMenuItemType | VizContextMenuOptionType,
    contextMenuInput: ContextMenuInputPoints,
    displayMode: DisplayMode,
    permissions: ObjectPermission,
    sessionService: SessionService,
    dataSources?: PinboardSourceDetail[],
    filterGroups?: FilterGroup[],
    containerId?: string,
    containerDataSourceIds?: string[],
): EnabledContextMenuOptionType => {
    const clickedPoint = contextMenuInput.clickedPoint;
    const selectedPoints = contextMenuInput.selectedPoints || [];
    const selectedAttributes = clickedPoint?.selectedAttributes || [];
    const selectedMeasures = clickedPoint?.selectedMeasures || [];
    const isMissingUnderlyingDataAccess =
        permissions?.dataSourceAccessLevel === AccessMode.NoAccess;
    const anyDateColumn = () =>
        selectedAttributes.some(attribute =>
            isColumnDateType(attribute?.column),
        );
    const dataSourceIds = _.map(dataSources, dataSource => dataSource.id);

    if (
        isMissingUnderlyingDataAccess &&
        VizContextMenuOptionsRequiringUnderlyingDataAccess.indexOf(optionId) !==
            -1
    ) {
        if (
            sessionService.isRequestAccessExpEnabled() &&
            dataSources !== undefined &&
            (optionId === VizContextMenuOptionType.DRILL ||
                optionId === GenericMenuItemType.SPOTIQ_ANALYZE ||
                optionId === GenericMenuItemType.SHOW_UNDERLYING_DATA ||
                optionId === GenericMenuItemType.CROSS_FILTER ||
                optionId === GenericMenuItemType.REMOVE_CROSS_FILTER)
        ) {
            return {
                enabled: true,
            };
        }
        return {
            enabled: false,
            explanation: translate(
                'vizContextMenu.disabledHelp.requiresUnderlyingAccess',
            ),
        };
    }

    if (
        optionId === VizContextMenuOptionType.DRILL ||
        optionId === GenericMenuItemType.SHOW_UNDERLYING_DATA
    ) {
        let disabledExplanation: any = null;
        const allPoints = [
            contextMenuInput.clickedPoint,
            ...contextMenuInput.selectedPoints,
        ].filter(Boolean);

        allPoints.forEach((drillPoint: ContextMenuInputPoint) => {
            if (disabledExplanation !== null) {
                return;
            }
            let measureColumnInputs = [];
            if (optionId === VizContextMenuOptionType.DRILL) {
                // For drilling, just drill the selected measures, if no measure is selected,
                // drill all measures.
                measureColumnInputs = drillPoint.selectedMeasures.length
                    ? drillPoint.selectedMeasures
                    : drillPoint.deselectedMeasures;
            } else {
                // For showing underlying data, pretend as if entire row is selected.
                measureColumnInputs = drillPoint.selectedMeasures.concat(
                    drillPoint.deselectedMeasures,
                );
            }

            const measureColumns = measureColumnInputs.map(vDV => vDV.column);
            const hasAnyGrowthCol = measureColumns.some(column => {
                return isGrowth(column);
            });
            if (hasAnyGrowthCol) {
                disabledExplanation =
                    optionId === VizContextMenuOptionType.DRILL
                        ? translate(
                              'vizContextMenu.disabledHelp.growthMeasureDrillDown',
                          )
                        : translate(
                              'vizContextMenu.disabledHelp.growthMeasureUnderlyingData',
                          );
            }
            if (contextMenuInput.selectedPoints.length > 1) {
                disabledExplanation =
                    optionId === VizContextMenuOptionType.DRILL
                        ? translate(
                              'vizContextMenu.disabledHelp.drillDownMultipleRows',
                          )
                        : translate(
                              'vizContextMenu.disabledHelp.underlyingDataMultipleRow',
                          );
            }
        });

        if (!_.isNull(disabledExplanation)) {
            return {
                enabled: false,
                explanation: disabledExplanation,
            };
        }
    }

    if (optionId === GenericMenuItemType.INCLUDE) {
        if (selectedAttributes?.length === 0 || selectedPoints.length > 1) {
            return {
                enabled: false,
                explanation: translate(
                    'vizContextMenu.disabledHelp.disabledInclude',
                ),
            };
        }
        if (anyDateColumn()) {
            return {
                enabled: false,
                explanation: translate(
                    'vizContextMenu.disabledHelp.dateColumn',
                ),
            };
        }
    }

    if (optionId === GenericMenuItemType.EXCLUDE) {
        if (selectedAttributes?.length !== 1 || selectedPoints.length > 1) {
            return {
                enabled: false,
                explanation:
                    displayMode === DisplayMode.ChartMode
                        ? translate(
                              'vizContextMenu.disabledHelp.disabledExcludeInChart',
                          )
                        : translate(
                              'vizContextMenu.disabledHelp.disabledExcludeInTable',
                          ),
            };
        }
        if (anyDateColumn()) {
            return {
                enabled: false,
                explanation: translate(
                    'vizContextMenu.disabledHelp.dateColumn',
                ),
            };
        }
    }

    if (optionId === GenericMenuItemType.SHOW_UNDERLYING_DATA) {
        // currently we don't show formula columns in the leaf level data
        // (i.e. all the formula columns are excluded). if there series
        // has only formula columns this would result in an empty view
        // hence we disable "show underlying data" option for such cases
        // const cols = clickedPoint.getAllColumnInputValues().map(iv => iv.column);
        const allColumnsAreFormulae = clickedPoint
            ?.getAllColumnInputValues()
            .map(iv => iv.column)
            .every(col => isFilterColumn(col));
        if (allColumnsAreFormulae) {
            return {
                enabled: false,
                explanation: translate(
                    'vizContextMenu.disabledHelp.allFormula',
                ),
            };
        }
        if (
            displayMode === DisplayMode.TableMode &&
            selectedPoints.length > 1
        ) {
            return {
                enabled: false,
                explanation: translate(
                    'vizContextMenu.disabledHelp.underlyingDataMultipleRow',
                ),
            };
        }
    }

    if (optionId === GenericMenuItemType.SPOTIQ_ANALYZE) {
        if (
            (selectedPoints[0]?.selectedMeasures?.length ||
                clickedPoint?.selectedMeasures?.length) &&
            !_.isEmpty(getUserSelectionPoints(contextMenuInput))
        ) {
            return {
                enabled: true,
            };
        }
        return {
            enabled: false,
            explanation: translate(
                'vizContextMenu.disabledHelp.nonMeasureColumn',
            ),
        };
    }

    if (optionId === GenericMenuItemType.CROSS_FILTER) {
        if (!dataSourceIds.includes(containerDataSourceIds[0])) {
            return {
                enabled: false,
                explanation: translate(
                    'vizContextMenu.disabledHelp.crossFilter.noWorksheet',
                ),
            };
        }
        if (
            selectedPoints.length > 1 ||
            (displayMode === DisplayMode.TableMode &&
                selectedAttributes.length + selectedMeasures.length > 1)
        ) {
            return {
                enabled: false,
                explanation: translate(
                    'vizContextMenu.disabledHelp.crossFilter.multipleCellSelected',
                ),
            };
        }
        if (selectedAttributes?.length === 0) {
            return {
                enabled: false,
                explanation: translate(
                    'vizContextMenu.disabledHelp.crossFilter.noAttributeColumn',
                ),
            };
        }
        // columns which can be filtered
        const filterableColumns = getFilterableColumns(
            selectedAttributes,
            dataSources,
            filterGroups,
        );
        if (!filterableColumns?.length) {
            return {
                enabled: false,
                explanation: translate(
                    'vizContextMenu.disabledHelp.crossFilter.noAttributeColumn',
                ),
            };
        }

        const sourceContainerIdByLogicalColumnId = new Map<string, string>();
        _.forEach(filterGroups, filterGroup => {
            sourceContainerIdByLogicalColumnId.set(
                filterGroup.groupId.logicalColumnId,
                filterGroup.sourceContainerId,
            );
        });
        // column which is already filtered from some other source container
        const isAnyColumnNonFilterable = _.some(
            filterableColumns,
            filterableColumn => {
                const columnId = getSelectedColumnId(filterableColumn);
                const sourceContainerId = sourceContainerIdByLogicalColumnId.get(
                    columnId,
                );
                return sourceContainerId && sourceContainerId !== containerId;
            },
        );
        if (isAnyColumnNonFilterable) {
            return {
                enabled: false,
                explanation: translate(
                    'vizContextMenu.disabledHelp.crossFilter.sameColumn',
                ),
            };
        }
    }
    if (optionId === GenericMenuItemType.REMOVE_CROSS_FILTER) {
        if (selectedAttributes?.length === 0 || selectedPoints?.length > 1) {
            return {
                enabled: false,
                explanation: translate(
                    'vizContextMenu.disabledHelp.crossFilter.noAttributeColumn',
                ),
            };
        }
        if (!dataSourceIds.includes(containerDataSourceIds[0])) {
            return {
                enabled: false,
                explanation: translate(
                    'vizContextMenu.disabledHelp.crossFilter.noWorksheet',
                ),
            };
        }
    }

    return {
        enabled: true,
    };
};

export function getFilterColumnAndContentListForSelectedAttributes(
    contextMenuInput: ContextMenuInputPoints,
    isExclude = false,
): {
    filterContent: FilterContentInput[];
    filterGroup: FilterGroup;
}[] {
    const selectedMenuPoints: ContextMenuInputValue[] =
        contextMenuInput.clickedPoint.selectedAttributes;
    return selectedMenuPoints.map((menuPoint: ContextMenuInputValue) => {
        const filterContent = {
            ...getFilterContent(menuPoint),
            negate: isExclude,
        };
        return {
            filterContent: [filterContent],
            filterGroup: getFilterGroup(menuPoint.column),
        };
    });
}

export function getFilterColumnAndContentListForClickedPoint(
    menuPoints: ContextMenuInputValue[],
    isLogicalColumn = false,
): {
    filterContent: FilterContentInput[];
    filterGroup: FilterGroup;
}[] {
    return menuPoints.map((menuPoint: ContextMenuInputValue) => {
        const filterContent = {
            ...getFilterContent(menuPoint),
        };
        return {
            filterContent: [filterContent],
            filterGroup: getFilterGroup(menuPoint.column, isLogicalColumn),
        };
    });
}

/**
 * get visible context menu action lists
 * @param contextMenuInput clicked and selected points
 * @param hiddenActions hidden actions
 * @param containerId container id of visualization
 * @param filterGroups applied filters
 * @param linkedFilters link filters
 */
export function getVisibleContextMenuItems(
    contextMenuInput: ContextMenuInputPoints,
    hiddenActions: (GenericMenuItemType | VizContextMenuOptionType)[] = [],
    actionList: string[],
    containerId?: string,
    filterGroups?: FilterGroup[],
    dataSources?: PinboardSourceDetail[],
    linkedFilters?: LinkedFilterColumn[],
    displayMode?: DisplayMode,
): string[] {
    const vizContextMenuOption = [...actionList];

    // remove hidden actions from list
    _.pullAll(vizContextMenuOption, hiddenActions);

    if (displayMode === DisplayMode.TableMode) {
        _.pullAll(vizContextMenuOption, [
            GenericMenuItemType.RUN_CHANGE_ANALYSIS,
        ]);
    }

    // if containerId exist we are checking cross filter action items visibility for liveboard viz
    if (!containerId) {
        // If not a liveboard viz then remove cross filter related actions
        _.pullAll(vizContextMenuOption, [
            GenericMenuItemType.CROSS_FILTER,
            GenericMenuItemType.REMOVE_CROSS_FILTER,
        ]);
    } else {
        // For liveboard viz
        // Map of filter props by column id, also filtered by source container
        const filterProps = getFilterPropsByColumnIdMap(
            filterGroups,
            containerId,
        );
        const { selectedAttributes } = contextMenuInput.clickedPoint || {};
        const linkFilterMap = getPrimaryColumnBySecondaryColumn(linkedFilters);
        let isAnyColumnFilterableInSelectedPoint = false;
        let isAnyColumnAlreadyFiltered = false;
        const isSelectedColumnFiltered = _.every(
            selectedAttributes,
            selectedAttribute => {
                const updatedSelectedAttribute = _.cloneDeep(selectedAttribute);
                const columnId = getSelectedColumnId(selectedAttribute);
                if (linkFilterMap.has(columnId)) {
                    const primaryColumnId = linkFilterMap.get(columnId);
                    _.set(
                        updatedSelectedAttribute,
                        'column.referencedColumns[0].guid',
                        primaryColumnId,
                    );
                }
                const isFilterable = isColumnFilterable(
                    selectedAttribute,
                    dataSources,
                    new Map(),
                );
                if (isFilterable) {
                    isAnyColumnFilterableInSelectedPoint = true;
                    const colId = getSelectedColumnId(updatedSelectedAttribute);
                    const filterProp = filterProps.get(colId);
                    if (filterProp) {
                        isAnyColumnAlreadyFiltered = true;
                        return _.includes(
                            filterProp?.filterValues,
                            _.toLower(updatedSelectedAttribute?.value),
                        );
                    }
                }
                return true;
            },
        );
        // Note: we will remove cross filter option when all 3 conditions are true for selected data point
        // 1. At least 1 attribute column which can be filtered should be present.
        // 2. At least 1 column should be filtered from this container.
        // 3. The value of column in point 2 should be present in cross filtered values.
        if (
            isAnyColumnFilterableInSelectedPoint &&
            isAnyColumnAlreadyFiltered &&
            isSelectedColumnFiltered
        ) {
            _.pull(vizContextMenuOption, GenericMenuItemType.CROSS_FILTER);
        } else {
            _.pull(
                vizContextMenuOption,
                GenericMenuItemType.REMOVE_CROSS_FILTER,
            );
        }
    }
    return vizContextMenuOption;
}

/**
 * get visible context menu action lists with disabled properties
 * @param contextMenuInput clicked and selected points
 * @param displayMode viz display mode
 * @param permissions liveboard permission
 * @param hiddenActions hidden actions
 * @param containerId container id of visualization
 * @param dataSources liveboard data sources
 * @param filterGroups applied filters
 * @param linkedFilters link filters
 */
export const getContextMenuItems = (
    contextMenuInput: ContextMenuInputPoints,
    displayMode: DisplayMode,
    permissions: ObjectPermission,
    actionList: string[],
    sessionService: SessionService,
    hiddenActions?: (GenericMenuItemType | VizContextMenuOptionType)[],
    containerId?: string,
    dataSources?: PinboardSourceDetail[],
    filterGroups?: FilterGroup[],
    linkedFilters?: LinkedFilterColumn[],
    containerDataSourceIds?: string[],
): ContextMenuOption[] => {
    const visibleContextMenuItems = getVisibleContextMenuItems(
        contextMenuInput,
        hiddenActions,
        actionList,
        containerId,
        filterGroups,
        dataSources,
        linkedFilters,
        displayMode,
    );

    const contextMenuItems: ContextMenuOption[] = _.map(
        visibleContextMenuItems,
        optionID => {
            const enabilityAndExplanation = getContextMenuItemsConfig(
                optionID as GenericMenuItemType | VizContextMenuOptionType,
                contextMenuInput,
                displayMode,
                permissions,
                sessionService,
                dataSources,
                filterGroups,
                containerId,
                containerDataSourceIds,
            );

            return {
                id: optionID as GenericMenuItemType | VizContextMenuOptionType,
                isDisabled: !enabilityAndExplanation.enabled,
                disabledExplanation: enabilityAndExplanation.explanation,
            };
        },
    );

    return contextMenuItems;
};

// TODO : Enforce strong typing
export function getVisualizationType(
    displayMode: DisplayMode,
    vizModel: TableViz | ChartViz,
): string {
    return displayMode === DisplayMode.ChartMode
        ? getChartType(vizModel as ChartViz)
        : ObjectType.Table;
}

export function getContextMenuOptionsBasedOnVisualisation(
    displayMode: DisplayMode,
    vizModel: TableViz | ChartViz,
    sessionService: SessionService,
    pageContext?: AnswerPageContext,
): string[] {
    // only if pinboard v2 is enabled, session service has a value, else is undefined
    const isNewAnswerAndPinboardExperienceEnabled =
        sessionService &&
        sessionService.isAnswerV2Enabled() &&
        sessionService.isPinboardV2Enabled();
    let optionTypes: string[];
    switch (getVisualizationType(displayMode, vizModel)) {
        case ObjectType.Table:
            optionTypes = DefaultVizContextMenuOptionTypesForTable;
            break;
        case ChartType.PivotTable: // Copy to clipboard for Pivot tables is supported only only when new answer and pinboard experience is enabled
            optionTypes = isNewAnswerAndPinboardExperienceEnabled
                ? DefaultVizContextMenuOptionTypesForPivotTable
                : DefaultVizContextMenuOptionTypesForChart;
            break;
        default:
            optionTypes = DefaultVizContextMenuOptionTypesForChart;
    }
    // Drill down, Show underlying data and SpotIQ analyze are not applicable
    // while building or editing an input table, so suppress them in that
    // context.
    if (
        pageContext === AnswerPageContext.INPUT_TABLE &&
        isInputTableVisible()
    ) {
        optionTypes = optionTypes.filter(
            optionType =>
                optionType !== VizContextMenuOptionType.DRILL &&
                optionType !== GenericMenuItemType.SHOW_UNDERLYING_DATA &&
                optionType !== GenericMenuItemType.SPOTIQ_ANALYZE,
        );
    }
    // Filter to a new array (the Default* arrays are shared module state).
    // sessionService can be undefined at runtime (see above), so guard it.
    if (sessionService && !sessionService.canAnalyzeData()) {
        return optionTypes.filter(
            optionType =>
                optionType !== VizContextMenuOptionType.DRILL &&
                optionType !== GenericMenuItemType.SHOW_UNDERLYING_DATA &&
                optionType !== GenericMenuItemType.RUN_CHANGE_ANALYSIS,
        );
    }
    return optionTypes;
}
/**
 * Function to handle custom values for null and empty values override.
 */
const getCaptionLabel = (column: AnswerColumn, value: any) => {
    // Check if the value matches the placeholder for empty values.
    if (value === translate('NULL_VALUE_PLACEHOLDER_LABEL')) {
        const nullValue = getNullValuePlaceHolder(); // Retrieve the override for null values set any.

        // If override value is set return it.
        if (nullValue !== translate('NULL_VALUE_PLACEHOLDER_LABEL')) {
            return nullValue;
        }
    }

    // Check if the value matches the placeholder for empty values.
    if (value === translate('EMPTY_VALUE_PLACEHOLDER_LABEL')) {
        const emptyValue = getEmptyValuePlaceHolder(); // Retrieve the override for empty values.

        // If override value is set return it.
        if (emptyValue !== translate('EMPTY_VALUE_PLACEHOLDER_LABEL')) {
            return emptyValue;
        }
    }

    // For all other cases, return the formatted value for the column and value.
    return getFormattedValue(column, value);
};

// TODO: define strong types
function getVizContextMenuItems(
    vizModel: ChartViz | TableViz,
    contextMenuInput: ContextMenuInputPoints,
    closeContextMenuCallback: () => void,
    onAction: (
        event: GenericMenuItemType | VizContextMenuOptionType,
        visualizationType: string,
        contextMenuPointValue?: ContextMenuInputValue,
    ) => void,
    onIncludeOrExclude: (
        filterColumnAndContentList: FilterColumnAndContent[],
    ) => void,
    t: any,
    displayMode: DisplayMode,
    permissions: ObjectPermission,
    getAnswerMixpanelProperties: () => any,
    actionList: string[],
    openDrillDownMenu?: () => void,
    getVisibleActionItems?: (
        contextMenuInput: ContextMenuInputPoints,
        displayMode: DisplayMode,
        permissions: ObjectPermission,
        actionList: any[],
        sessionService?: SessionService,
        isFromChangeAnalysisModel?: boolean,
        hasSinglePointKPIValue?: boolean,
    ) => ContextMenuOption[],
    onShowUnderlyingData?: (
        filterColumnAndContentList: FilterColumnAndContent[],
        unFilterColumnAndContentList: FilterColumnAndContent[],
    ) => void,
    onSpotIqAnalyze?: (data: any) => void,
    onRunChangeAnalysis?: (clickedPoint: any, selectedPoints: any) => void,
    customContextActions: any = [],
    codeBasedContextMenuCustomActions: CustomAction[] = [],
    onCustomAction?: (
        data: EmbedActionConfig | CustomAction,
        isCodeBased?: boolean,
    ) => void,
    metricsService?: MetricsService,
    embedConfig?: EmbedContextOptions,
    disabledActions?: Set<string>,
    embedDisabledReason?: string,
    sessionService?: SessionService,
    applyOrRemoveCrossFilter?: (
        contextMenuInput: ContextMenuInputPoints,
        action: GenericMenuItemType,
        vizType?: string,
    ) => void,
    onIterativeChangeAnalysis?: (
        columnGuid: string,
        attributeValue: string,
        attributeName: string,
    ) => void,
    isFromChangeAnalysisModel?: boolean,
    setStateRequestAccessDataSource?: React.Dispatch<
        React.SetStateAction<boolean>
    >,
    shouldShowUnderlyingDataAccessReqModal?: boolean,
    hasSinglePointKPIValue?: boolean,
): { [key: string]: ActionMenuItem } {
    const embedActionStateMap = getEmbedActionStateMap(
        embedConfig || ({} as EmbedContextOptions),
    );
    const visibleContextMenuActions = getVisibleActionItems(
        contextMenuInput,
        displayMode,
        permissions,
        actionList,
        sessionService,
        isFromChangeAnalysisModel,
        hasSinglePointKPIValue,
    );

    const vizType = getVisualizationType(displayMode, vizModel);

    const isCopyToClipboardDisabled = (): boolean => {
        if (
            contextMenuInput.selectedPoints.length >= 1 ||
            contextMenuInput.clickedPoint.selectedMeasures.length >= 1 ||
            contextMenuInput.clickedPoint.selectedAttributes.length >= 1
        )
            return false;
        return true;
    };

    const shouldDisableCCAForColorValues = shouldDisableCCAForDifferentColorValues(
        vizModel as ChartViz,
        contextMenuInput,
    );

    const contextMenuItems = _.reduce(
        visibleContextMenuActions,
        (inputMenuItems, visibleContextMenuAction) => {
            const {
                id,
                isDisabled,
                disabledExplanation,
            } = visibleContextMenuAction;
            const menuItems: any = inputMenuItems;
            const clickedPoint = contextMenuInput.clickedPoint;
            let caption: string;

            switch (id) {
                case GenericMenuItemType.DO_ITERATIVE_ANALYSIS:
                    if (
                        clickedPoint &&
                        _.size(clickedPoint.selectedAttributes) === 1
                    ) {
                        const column =
                            clickedPoint.selectedAttributes[0].column;
                        const value = clickedPoint.selectedAttributes[0].value;
                        const columnName = column.name;
                        caption = t('vizContextMenu.analyzeAttribute', {
                            1: getFormattedValue(column, value),
                        });
                        menuItems[id] = {
                            id: GenericMenuItemType.DO_ITERATIVE_ANALYSIS,
                            label: caption,
                            onClick: () => {
                                const columnGuid =
                                    column.referencedColumns[0].guid;
                                // Date is given in epoch, this should be converted into
                                // proper string value so that it can be fed to applySuggestions api
                                // Custom calendar is applied for a specific column
                                // If it is a non date or time column  this value will be null
                                // so we can directly use the 'value' as fallback
                                const formattedAttributeValue = getFormattedDateForApplySuggestions(
                                    column,
                                    value,
                                );

                                onIterativeChangeAnalysis(
                                    columnGuid,
                                    formattedAttributeValue ?? value,
                                    columnName,
                                );
                            },
                            itemDisabled:
                                isDisabled ||
                                embedActionStateMap?.get(
                                    Action.EnableIterativeChangeAnalysis,
                                ).isDisabled,
                            itemDisabledTooltip: disabledExplanation,
                            hidden: embedActionStateMap?.get(
                                Action.EnableIterativeChangeAnalysis,
                            ).isHidden,
                        };
                    }
                    break;
                case VizContextMenuOptionType.DRILL:
                    menuItems[id] = {
                        id: VizContextMenuOptionType.DRILL,
                        label: t('Drill_down'),
                        onClick: () => {
                            if (shouldShowUnderlyingDataAccessReqModal) {
                                setStateRequestAccessDataSource(true);
                                closeContextMenuCallback();
                            } else {
                                // onAction(VizContextMenuOptionType.DRILL);
                                const answerMixpanelProperties = getAnswerMixpanelProperties();
                                metricsService.collectEvent(
                                    new UserAction(
                                        userActionTypeEnum.DRILL_DOWN,
                                    ),
                                    {
                                        vizChartType: vizType,
                                        vizType: displayMode,
                                        answerPageContext:
                                            answerMixpanelProperties?.pageContext,
                                        fromColumnIds: getDrillFromColumnIds(
                                            contextMenuInput,
                                        ),
                                        // No toColumnId here: the drill target
                                        // is picked in the menu this opens.
                                        dataSourceId:
                                            answerMixpanelProperties?.worksheetId,
                                        vizId: vizModel?.id,
                                    },
                                );
                                openDrillDownMenu();
                            }
                        },
                        itemDisabled: isDisabled,
                        itemDisabledTooltip: disabledExplanation,
                    };
                    break;
                case GenericMenuItemType.SPOTIQ_ANALYZE:
                    menuItems[id] = {
                        id: GenericMenuItemType.SPOTIQ_ANALYZE,
                        label: t('spotIQAnalyze'),
                        onClick: () => {
                            if (shouldShowUnderlyingDataAccessReqModal) {
                                setStateRequestAccessDataSource(true);
                            } else {
                                onSpotIqAnalyze(contextMenuInput);
                            }
                            closeContextMenuCallback();
                        },
                        itemDisabled: isDisabled,
                        itemDisabledTooltip: disabledExplanation,
                        hidden: !onSpotIqAnalyze,
                    };
                    break;
                case GenericMenuItemType.RUN_CHANGE_ANALYSIS:
                    menuItems[id] = {
                        id: GenericMenuItemType.RUN_CHANGE_ANALYSIS,
                        label: t('RUN_CHANGE_ANALYSIS'),
                        onClick: () => {
                            onRunChangeAnalysis(
                                contextMenuInput.clickedPoint,
                                contextMenuInput.selectedPoints,
                            );
                            closeContextMenuCallback();
                        },
                        itemDisabled:
                            shouldDisableCCAForColorValues.shouldDisable ||
                            isDisabled ||
                            shouldDisableForEmbed(
                                embedConfig,
                                Action.EnableContextualChangeAnalysis,
                            ),
                        itemDisabledTooltip:
                            shouldDisableCCAForColorValues?.reason ||
                            disabledExplanation,
                        hidden:
                            !onRunChangeAnalysis ||
                            shouldHideForEmbed(
                                embedConfig,
                                Action.EnableContextualChangeAnalysis,
                            ),
                    };
                    break;
                case GenericMenuItemType.SHOW_UNDERLYING_DATA:
                    // In case of table, the row could be partially selected, but we just pretend as
                    // if entire row was selected. So we merge selected and deselected attributes
                    // and measures. For charts, you can only select on entire row
                    // (i.e. A point on chart). So for charts deselectedAttributes and
                    // deselectedMeasures are always empty.
                    menuItems[id] = {
                        id: GenericMenuItemType.SHOW_UNDERLYING_DATA,
                        label: t('Show_underlying'),
                        itemDisabled: isDisabled,
                        itemDisabledTooltip: disabledExplanation,
                        onClick: () => {
                            if (shouldShowUnderlyingDataAccessReqModal) {
                                setStateRequestAccessDataSource(true);
                            } else {
                                onShowUnderlyingData(
                                    // following args are needed by pinboard v1
                                    getFilterColumnAndContentListForClickedPoint(
                                        clickedPoint.selectedAttributes.concat(
                                            clickedPoint.deselectedAttributes,
                                        ),
                                    ),
                                    getFilterColumnAndContentListForClickedPoint(
                                        clickedPoint.selectedMeasures.concat(
                                            clickedPoint.deselectedMeasures,
                                        ),
                                    ),
                                );
                            }
                            closeContextMenuCallback();
                        },
                    };
                    break;
                case GenericMenuItemType.INCLUDE:
                    if (
                        clickedPoint &&
                        _.size(clickedPoint.selectedAttributes) === 1
                    ) {
                        const column =
                            clickedPoint.selectedAttributes[0].column;
                        const value = clickedPoint.selectedAttributes[0].value;
                        caption = t('vizContextMenu.includeValue', {
                            1: getCaptionLabel(column, value),
                        });
                    } else if (displayMode === DisplayMode.ChartMode) {
                        caption = t('vizContextMenu.includePoint');
                    } else {
                        caption = t('vizContextMenu.includeValues');
                    }

                    menuItems[id] = {
                        id: GenericMenuItemType.INCLUDE,
                        label: caption,
                        onClick: () => {
                            const filterColumnAndContentList = getFilterColumnAndContentListForSelectedAttributes(
                                contextMenuInput,
                            );
                            _.forEach(
                                filterColumnAndContentList,
                                ({ filterGroup, filterContent }) => {
                                    const { columnInfo } = filterGroup;
                                    collectInitiateFilterAdditionEvent(
                                        collectEventWithMixpanelData(
                                            metricsService,
                                            getAnswerMixpanelProperties,
                                        ),
                                        INITIATE_FILTER_ADDITION_SOURCES.RIGHT_CLICK_TO_INCLUDE_EXCLUDE_FILTER,
                                        getMetricsColumnDataType(
                                            columnInfo.dataType,
                                            columnInfo.type,
                                            columnInfo.timeBucket,
                                        ),
                                    );
                                    collectAddFilterEvent(
                                        collectEventWithMixpanelData(
                                            metricsService,
                                            getAnswerMixpanelProperties,
                                        ),
                                        getMetricsColumnDataType(
                                            columnInfo.dataType,
                                            columnInfo.type,
                                            columnInfo.timeBucket,
                                        ),
                                        ADD_FILTER_SOURCES.RIGHT_CLICK_ONLY_INCLUDE_FILTER,
                                        _.head(filterContent)?.value?.length,
                                    );
                                },
                            );
                            onIncludeOrExclude(filterColumnAndContentList);
                            closeContextMenuCallback();
                        },
                        itemDisabled: isDisabled,
                        itemDisabledTooltip: disabledExplanation,
                    };
                    break;
                case GenericMenuItemType.EXCLUDE:
                    if (clickedPoint?.selectedAttributes?.length === 1) {
                        const column =
                            clickedPoint.selectedAttributes[0].column;
                        const value = clickedPoint.selectedAttributes[0].value;
                        caption = t('vizContextMenu.excludeValue', {
                            1: getCaptionLabel(column, value),
                        });
                    } else {
                        caption = t('vizContextMenu.excludeValues');
                    }
                    menuItems[id] = {
                        id: GenericMenuItemType.EXCLUDE,
                        label: caption,
                        onClick: () => {
                            const filterColumnAndContentList = getFilterColumnAndContentListForSelectedAttributes(
                                contextMenuInput,
                                true,
                            );
                            _.forEach(
                                filterColumnAndContentList,
                                ({ filterGroup, filterContent }) => {
                                    const { columnInfo } = filterGroup;
                                    collectInitiateFilterAdditionEvent(
                                        collectEventWithMixpanelData(
                                            metricsService,
                                            getAnswerMixpanelProperties,
                                        ),
                                        INITIATE_FILTER_ADDITION_SOURCES.RIGHT_CLICK_TO_INCLUDE_EXCLUDE_FILTER,
                                        getMetricsColumnDataType(
                                            columnInfo.dataType,
                                            columnInfo.type,
                                            columnInfo.timeBucket,
                                        ),
                                    );
                                    collectAddFilterEvent(
                                        metricsService.collectEvent,
                                        getMetricsColumnDataType(
                                            columnInfo.dataType,
                                            columnInfo.type,
                                            columnInfo.timeBucket,
                                        ),
                                        ADD_FILTER_SOURCES.RIGHT_CLICK_EXCLUDE_FILTER,
                                        _.head(filterContent)?.value?.length,
                                    );
                                },
                            );
                            onIncludeOrExclude(filterColumnAndContentList);
                            closeContextMenuCallback();
                        },
                        itemDisabled: isDisabled,
                        itemDisabledTooltip: disabledExplanation,
                    };
                    break;
                case GenericMenuItemType.CROSS_FILTER:
                    menuItems[id] = {
                        id: GenericMenuItemType.CROSS_FILTER,
                        label: t('FILTER'),
                        onClick: () => {
                            if (shouldShowUnderlyingDataAccessReqModal) {
                                setStateRequestAccessDataSource(true);
                            } else {
                                applyOrRemoveCrossFilter(
                                    contextMenuInput,
                                    GenericMenuItemType.CROSS_FILTER,
                                    vizType,
                                );
                            }
                            closeContextMenuCallback();
                        },
                        itemDisabled: isDisabled,
                        itemDisabledTooltip: disabledExplanation,
                    };
                    break;
                case GenericMenuItemType.REMOVE_CROSS_FILTER:
                    menuItems[id] = {
                        id: GenericMenuItemType.REMOVE_CROSS_FILTER,
                        label: t('REMOVE_FILTER'),
                        onClick: () => {
                            if (shouldShowUnderlyingDataAccessReqModal) {
                                setStateRequestAccessDataSource(true);
                            } else {
                                applyOrRemoveCrossFilter(
                                    contextMenuInput,
                                    GenericMenuItemType.REMOVE_CROSS_FILTER,
                                );
                            }
                            closeContextMenuCallback();
                        },
                        itemDisabled: isDisabled,
                        itemDisabledTooltip: disabledExplanation,
                    };
                    break;
                case GenericMenuItemType.COPY_TO_CLIPBOARD:
                    menuItems[GenericMenuItemType.COPY_TO_CLIPBOARD] = {
                        id: GenericMenuItemType.COPY_TO_CLIPBOARD,
                        label: t('Copy_to'),
                        onClick: () => {
                            metricsService.collectEvent(
                                new UserAction(
                                    userActionTypeEnum.COPY_TO_CLIPBOARD,
                                ),
                                {},
                            );
                            onAction(
                                GenericMenuItemType.COPY_TO_CLIPBOARD,
                                vizType,
                            );
                            closeContextMenuCallback();
                        },
                        itemDisabledTooltip: translate(
                            'vizContextMenu.disabledHelp.copyToClipboard',
                        ),
                        itemDisabled: isCopyToClipboardDisabled(),
                    };
                    break;

                default:
                    throw new Error('Unhandled context menu type');
            }
            return menuItems;
        },
        {},
    );

    _.forEach(customContextActions, (customAction: any) => {
        contextMenuItems[customAction.detail.function] = {
            id: customAction.detail.function,
            label: customAction.name,
            onClick: () => {
                onCustomAction(customAction);
                closeContextMenuCallback();
            },
            itemDisabled: disabledActions?.has(customAction.detail.function),
            itemDisabledTooltip: embedDisabledReason,
        };
    });

    _.forEach(codeBasedContextMenuCustomActions, (customAction: any) => {
        contextMenuItems[customAction.id] = {
            id: customAction.id,
            label: customAction.name,
            onClick: () => {
                onCustomAction(customAction, true);
                closeContextMenuCallback();
            },
            itemDisabled: disabledActions?.has(customAction.id),
            itemDisabledTooltip: embedDisabledReason,
        };
    });

    return contextMenuItems as any;
}

const collectEventsForPivotContextActions = (
    customItems: ActionMenuItem,
    getAnswerMixpanelProperties: () => any,
) => {
    const answerCommonProperties = getAnswerMixpanelProperties();
    if (customItems.id === GenericMenuItemType.PIVOT_HEADER_EXPAND) {
        return collectEvent(
            new UserAction(userActionTypeEnum.PIVOT_CONTEXT_EXPAND_ALL),
            { ...answerCommonProperties },
        );
    }
    if (customItems.id === GenericMenuItemType.PIVOT_HEADER_COLLAPSE) {
        return collectEvent(
            new UserAction(userActionTypeEnum.PIVOT_CONTEXT_COLLAPSE_ALL),
            { ...answerCommonProperties },
        );
    }
    if (customItems.id === GenericMenuItemType.PIVOT_SORT) {
        return collectEvent(
            new UserAction(userActionTypeEnum.PIVOT_CONTEXT_SORT),
            { ...answerCommonProperties },
        );
    }
    if (customItems.id === GenericMenuItemType.PIVOT_CLEAR_SORT) {
        return collectEvent(
            new UserAction(userActionTypeEnum.PIVOT_CONTEXT_CLEAR_SORT),
            { ...answerCommonProperties },
        );
    }
    return null;
};

const wrapCustomContextMenuItems = (
    customContextMenuItems: ActionMenuItem[] = [],
    closeContextMenu: () => void,
    customStyles: any = {},
    getAnswerMixpanelProperties: () => any,
): ActionMenuItem[] =>
    customContextMenuItems.map(customItems => {
        const tempCustomItems = customItems;
        if (tempCustomItems.id === GenericMenuItemType.SEPARATOR) {
            tempCustomItems.className = _.get(customStyles, 'hasSeparator');
        }
        const customMenuOnClickCallback = tempCustomItems.onClick;
        tempCustomItems.onClick = () => {
            collectEventsForPivotContextActions(
                tempCustomItems,
                getAnswerMixpanelProperties,
            );
            customMenuOnClickCallback();
            closeContextMenu();
        };
        return tempCustomItems;
    });

export const getColumnForContextMenu = (
    model: ChartViz,
    contextMenuInputPoints: ContextMenuInputPoints,
): AnswerColumn => {
    // Return the first selected measure column if available
    const selectedMeasureColumn =
        contextMenuInputPoints.clickedPoint.selectedMeasures[0]?.column;
    if (selectedMeasureColumn) {
        return selectedMeasureColumn;
    }

    if (isCustomOrTsHostedChart(getChartType(model))) {
        // Try to get the selected attribute column since PT2.0 supports single attribute
        const selectedAttributeColumn =
            contextMenuInputPoints.clickedPoint.selectedAttributes[0]?.column;
        if (selectedAttributeColumn) {
            return selectedAttributeColumn;
        }
    }

    // fall back to first y-axis column for all other chart types
    return model?.config?.axisConfig?.[0]?.y?.[0];
};

export function getInputForChartContextMenu(
    model: ChartViz,
    contextMenuInputPoints: ContextMenuInputPoints,
    closeContextMenu: () => void,
    onOpenChartEditor: (colId: string) => void,
    onAction: (
        event: GenericMenuItemType | VizContextMenuOptionType,
        visualizationType: string,
    ) => void,
    onIncludeOrExclude: (
        filterColumnAndContentList: FilterColumnAndContent[],
    ) => void,
    t: any,
    permissions: ObjectPermission,
    actionList: string[],
    getAnswerMixpanelProperties: () => any,
    openDrillDownMenu: () => void,
    getVisibleActionItems?: (
        contextMenuInput: ContextMenuInputPoints,
        displayMode: DisplayMode,
        permissions: ObjectPermission,
        actionList: string[],
    ) => ContextMenuOption[],
    onShowUnderlyingData?: (
        filterColumnAndContentList: FilterColumnAndContent[],
        unFilterColumnAndContentList: FilterColumnAndContent[],
    ) => void,
    onSpotIqAnalyze?: (data: any) => void,
    onRunChangeAnalysis?: (clickedPoint: any, selectedPoints: any) => void,
    customStyles?: any,
    embedConfig?: EmbedContextOptions,
    customContextActions?: any,
    codeBasedContextMenuCustomActions?: CustomAction[],
    onCustomAction?: (data: any) => void,
    allowColumnEdit = true,
    metricsService?: MetricsService,
    disabledActions?: Set<string>,
    embedDisabledReason?: string,
    customContextMenuItems?: ActionMenuItem[],
    sessionService?: SessionService,
    applyOrRemoveCrossFilter?: (
        contextMenuInput: ContextMenuInputPoints,
        action: GenericMenuItemType,
    ) => void,
    setStateRequestAccessDataSource?: React.Dispatch<
        React.SetStateAction<boolean>
    >,
    shouldShowUnderlyingDataAccessReqModal?: boolean,
    onIterativeChangeAnalysis?: (
        columnGuid: string,
        attributeValue: string,
        attributeName: string,
    ) => void,
    isFromChangeAnalysisModel?: boolean,
    chartConfigParameters?: ChartConfigParameters,
    hasSinglePointKPIValue?: boolean,
): ActionMenuItemGroup[] {
    let menuItems = getVizContextMenuItems(
        model,
        contextMenuInputPoints,
        closeContextMenu,
        onAction,
        onIncludeOrExclude,
        t,
        DisplayMode.ChartMode,
        permissions,
        getAnswerMixpanelProperties,
        actionList,
        openDrillDownMenu,
        getVisibleActionItems,
        onShowUnderlyingData,
        onSpotIqAnalyze,
        onRunChangeAnalysis,
        customContextActions,
        codeBasedContextMenuCustomActions,
        onCustomAction,
        metricsService,
        embedConfig,
        disabledActions,
        embedDisabledReason,
        sessionService,
        applyOrRemoveCrossFilter,
        onIterativeChangeAnalysis,
        isFromChangeAnalysisModel,
        setStateRequestAccessDataSource,
        shouldShowUnderlyingDataAccessReqModal,
        hasSinglePointKPIValue,
    );

    const column = getColumnForContextMenu(model, contextMenuInputPoints);

    if (allowColumnEdit && canEditColumn(model, column)) {
        menuItems[GenericMenuItemType.SEPARATOR] = {
            id: GenericMenuItemType.SEPARATOR,
            label: null,
            icon: null,
            onClick: _.noop,
            className: _.get(customStyles, 'hasSeparator'),
        };

        menuItems[GenericMenuItemType.EDIT_MEASURE] = {
            id: GenericMenuItemType.EDIT_MEASURE,
            label: t('vizContextMenu.editColumn', {
                1: getName(column),
            }),
            onClick: () => {
                const answerCommonProperties = getAnswerMixpanelProperties();
                metricsService.collectEvent(
                    new UserAction(userActionTypeEnum.EDIT_COLUMN),
                    {
                        source: CONTEXT_MENU,
                        dataType: column.dataType,
                        ...answerCommonProperties,
                    },
                );
                onOpenChartEditor(getId(column));
                closeContextMenu();
                // collectEvent(new UserAction(userActionTypeEnum.EDIT_MEASURE), {
                //     source: userActionTypes.EDIT_MEASURE.actionParams.source.CONTEXT_MENU,
                //     id: column.getId()
                // });
            },
        };
    }
    if (embedConfig) {
        menuItems = getFilterContentEmbed(menuItems, embedConfig);
    }
    if (isCustomOrTsHostedChart(getChartType(model))) {
        menuItems = getFilterContentCustomChartSdk(
            menuItems,
            chartConfigParameters,
            getChartType(model),
        );
    }

    const wrappedCustomContextMenuItems = wrapCustomContextMenuItems(
        customContextMenuItems,
        closeContextMenu,
        customStyles,
        getAnswerMixpanelProperties,
    );

    const priorityCustomMenuItems = _.remove(
        wrappedCustomContextMenuItems,
        menuItem => {
            // Some custom menu items need to show up at the top of the context
            // menu. The order within these menu items is based on insert order.
            return menuItem.id === GenericMenuItemType.EXPLAIN_ANOMALY;
        },
    );

    return [
        {
            title: 'Context Menu',
            items: [
                ...priorityCustomMenuItems,
                ...Object.values(menuItems),
                ...wrappedCustomContextMenuItems,
            ],
        },
    ];
}

export function getInputForTableContextMenu(
    model: TableViz,
    contextMenuInputPoints: ContextMenuInputPoints,
    closeContextMenu: () => void,
    onOpenChartEditor = _.noop,
    onAction: (
        event: GenericMenuItemType | VizContextMenuOptionType,
        visualizationType: string,
    ) => void,
    onIncludeOrExclude: (
        filterColumnAndContentList: FilterColumnAndContent[],
    ) => void,
    t: any,
    permissions: ObjectPermission,
    actionList: string[],
    getAnswerMixpanelProperties: () => any,
    openDrillDownMenu: () => void,
    getVisibleActionItems: (
        contextMenuInput: ContextMenuInputPoints,
        displayMode: DisplayMode,
        permissions: ObjectPermission,
        actionList: string[],
    ) => ContextMenuOption[],
    onShowUnderlyingData?: (
        filterColumnAndContentList: FilterColumnAndContent[],
        unFilterColumnAndContentList: FilterColumnAndContent[],
    ) => void,
    onSpotIqAnalyze?: (filterColumnAndContentList: any) => void,
    onRunChangeAnalysis?: (clickedPoint: any, selectedPoints: any) => void,
    customStyles?: any,
    embedConfig?: EmbedContextOptions,
    customContextActions?: any,
    codeBasedContextMenuCustomActions?: any,
    onCustomAction?: (filterColumnAndContentList: any) => void,
    allowColumnEdit = true,
    metricsService?: MetricsService,
    disabledActions?: Set<string>,
    embedDisabledReason?: string,
    customContextMenuItems?: ActionMenuItem[],
    sessionService?: SessionService,
    applyOrRemoveCrossFilter?: (
        contextMenuInput: ContextMenuInputPoints,
        action: GenericMenuItemType,
    ) => void,
    setStateRequestAccessDataSource?: React.Dispatch<
        React.SetStateAction<boolean>
    >,
    shouldShowUnderlyingDataAccessReqModal?: boolean,
): ActionMenuItemGroup[] {
    let menuItems = getVizContextMenuItems(
        model,
        contextMenuInputPoints,
        closeContextMenu,
        onAction,
        onIncludeOrExclude,
        t,
        DisplayMode.TableMode,
        permissions,
        getAnswerMixpanelProperties,
        actionList,
        openDrillDownMenu,
        getVisibleActionItems,
        onShowUnderlyingData,
        onSpotIqAnalyze,
        onRunChangeAnalysis,
        customContextActions,
        codeBasedContextMenuCustomActions,
        onCustomAction,
        metricsService,
        embedConfig,
        disabledActions,
        embedDisabledReason,
        sessionService,
        applyOrRemoveCrossFilter,
        undefined,
        undefined,
        setStateRequestAccessDataSource,
        shouldShowUnderlyingDataAccessReqModal,
        false,
    );

    if (embedConfig) {
        menuItems = getFilterContentEmbed(menuItems, embedConfig);
    }
    return [
        {
            title: 'Context Menu',
            items: Object.values(menuItems),
        },
    ];
}

export const nTimesUpdateFilter = (
    contextMenuPoints: ContextMenuInputPoints,
) => {
    const selectedAttributes =
        contextMenuPoints?.clickedPoint?.selectedAttributes || [];
    return selectedAttributes.length > 0 ? selectedAttributes.length : 1;
};

// TODO(chetan): move to a common util
export const parseVizDataResponse = (
    dataResponse: any,
    displayMode: DisplayMode,
) => {
    const error = dataResponse.error;
    const loading = dataResponse.loading;
    const viz =
        displayMode === DisplayMode.ChartMode
            ? dataResponse.data?.chartViz
            : dataResponse.tableViz;
    return { error, loading, viz };
};

const DISPLAY_MODE_TO_CONTEXT_ACTIONS_GETTER: Partial<Record<
    DisplayMode,
    any
>> = {
    [DisplayMode.ChartMode]: getInputForChartContextMenu,
    [DisplayMode.TableMode]: getInputForTableContextMenu,
};

export const getMenuItemsGetterFn = (displayMode: DisplayMode) => {
    return DISPLAY_MODE_TO_CONTEXT_ACTIONS_GETTER[displayMode];
};

export const getFilterGroupByAnswerColumnId = (
    filterGroups: FilterGroup[],
    answerColumnId: string,
) =>
    _.find(
        filterGroups,
        filterGroup => filterGroup.groupId.answerColumnId === answerColumnId,
    );

/**
 * Process date filter by getting date range input and content
 */
const processDateFilter = (
    filterGroup: FilterGroup,
    filterContent: FilterContentInput[],
): FilterContentInput[] => {
    const { startDate, endDate, filterOperator } = getDateRangeFilterInput(
        filterGroup?.filters,
    );

    const appliedFilters = getDateRangeFilterContent(
        startDate,
        endDate,
        filterGroup?.filters[0]?.filterId,
        filterOperator,
    );

    if (!appliedFilters) {
        return filterContent;
    }

    return filterContent.map((content: FilterContentInput) => ({
        ...content,
        filterId: filterGroup?.filters[0]?.filterId,
    }));
};

/**
 * Process attribute filter by getting include/exclude filter input
 */
const processAttributeFilter = (
    filterGroup: FilterGroup,
    filterContent: FilterContentInput[],
): FilterContentInput[] => {
    const { includeFilter, excludeFilter } = getAttributeFilterInput(
        filterGroup?.filters,
    );

    // filterContent is an array of FilterContentInput, but only have one filterContent in the array
    // so we can access first element of the array
    const isNegated = filterContent[0]?.negate;
    const appliedFilters = isNegated ? excludeFilter : includeFilter;

    if (!appliedFilters) {
        return filterContent;
    }

    let appliedFilterValues: FilterColumnValueInput[] = [];

    // Only append excluded filter values, not included ones
    if (isNegated) {
        // filterContent is an array of FilterContentInput, but always have one filterContent in the array
        appliedFilterValues = appliedFilters?.filterContent[0]?.value?.map(
            val => stringToFilterColumnValueInput(val?.key),
        );
    }

    return filterContent.map((content: FilterContentInput) => ({
        ...content,
        value: [...content.value, ...appliedFilterValues],
        filterId: appliedFilters?.filterId,
    }));
};

// onIncludeOrExclude in ContextMenuContainer is for 'attributes' only,
// whether for negated = true / false. Therefore, we can get filterId
// from filterGroups w/o the concern of logicalColumnId in DateRangeFilters.
export const addFilterIdIfPresent = (
    filterColumnAndContentList: FilterColumnAndContent[],
    filterGroups: FilterGroup[],
): FilterColumnAndContent[] =>
    filterColumnAndContentList.map(
        (filterColumnAndContent: FilterColumnAndContent) => {
            const answerColumnId =
                filterColumnAndContent?.filterGroup?.groupId?.answerColumnId;
            const filterGroup = getFilterGroupByAnswerColumnId(
                filterGroups,
                answerColumnId,
            );

            if (!filterGroup) {
                return filterColumnAndContent;
            }

            const filterType = getFilterType(
                filterGroup?.columnInfo?.dataType,
                filterGroup?.columnInfo?.type,
                filterGroup?.columnInfo?.timeBucket,
            );

            const isDateFilter = filterType === FILTER_TYPE.DATE_RANGE_FILTER;
            const updatedFilterContent = isDateFilter
                ? processDateFilter(
                      filterGroup,
                      filterColumnAndContent?.filterContent,
                  )
                : processAttributeFilter(
                      filterGroup,
                      filterColumnAndContent?.filterContent,
                  );

            return {
                ...filterColumnAndContent,
                filterContent: updatedFilterContent,
            };
        },
    );

/**
 * get hidden context menu options for monitoring
 */

export const getHiddenContextMenuOptionsForMonitoring = (
    contextMenuInputPoints: ContextMenuInputPoints,
    sessionService?: SessionService,
    isFromChangeAnalysisModel = false,
    hasSinglePointKPIValue = false,
) => {
    const hiddenContextMenuActions: (
        | GenericMenuItemType
        | VizContextMenuOptionType
    )[] = [];

    const selectedPoints = contextMenuInputPoints?.selectedPoints;

    if (
        selectedPoints?.length !== 2 ||
        // Two context menu points correspond to different plotted measures
        selectedPoints[0]?.selectedMeasures[0]?.column?.id !==
            selectedPoints[1]?.selectedMeasures[0]?.column?.id ||
        (sessionService && !sessionService.isContextualChangeAnalysisEnabled())
    ) {
        hiddenContextMenuActions.push(GenericMenuItemType.RUN_CHANGE_ANALYSIS);
    }

    if (
        (sessionService && !sessionService.isA3Enabled()) ||
        selectedPoints?.length !== 1
    ) {
        hiddenContextMenuActions.push(GenericMenuItemType.SPOTIQ_ANALYZE);
    }

    if (
        (isFromChangeAnalysisModel &&
            sessionService &&
            !sessionService.isIterativeCCAEnabled()) ||
        !isFromChangeAnalysisModel ||
        hasSinglePointKPIValue
    ) {
        hiddenContextMenuActions.push(
            GenericMenuItemType.DO_ITERATIVE_ANALYSIS,
        );
    }

    if (isFromChangeAnalysisModel && sessionService.isIterativeCCAEnabled()) {
        hiddenContextMenuActions.push(
            GenericMenuItemType.EXCLUDE,
            GenericMenuItemType.INCLUDE,
            GenericMenuItemType.SPOTIQ_ANALYZE,
            GenericMenuItemType.SHOW_UNDERLYING_DATA,
            VizContextMenuOptionType.DRILL,
        );
    }
    return hiddenContextMenuActions;
};

/**
 * get default visible context menu actions for answer
 * Please note that this will not be used by Liveboards.
 * Check getLiveboardContextMenuActions in pinboard-viz.container.tsx
 * @param contextMenuInputPoints clicked and selected points
 * @param displayMode viz display mode
 * @param permissions liveboard permission
 * @param sessionService session service
 */
export const getVisibleContextMenuActionItems = (
    contextMenuInputPoints: ContextMenuInputPoints,
    displayMode: DisplayMode,
    permissions: ObjectPermission,
    actionList: string[],
    sessionService?: SessionService,
    isFromChangeAnalysisModel?: boolean,
    hasSinglePointKPIValue?: boolean,
) => {
    let hiddenContextMenuActions: (
        | GenericMenuItemType
        | VizContextMenuOptionType
    )[] = [];
    const hiddenContextMenuOptionsForMonitoring = getHiddenContextMenuOptionsForMonitoring(
        contextMenuInputPoints,
        sessionService,
        isFromChangeAnalysisModel,
        hasSinglePointKPIValue,
    );
    hiddenContextMenuActions = [
        ...hiddenContextMenuActions,
        ...hiddenContextMenuOptionsForMonitoring,
    ];
    return getContextMenuItems(
        contextMenuInputPoints,
        displayMode,
        permissions,
        actionList,
        sessionService,
        hiddenContextMenuActions,
    );
};
